import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, inject, signal } from '@angular/core';
import {
  KcButtonComponent,
  KcDialogComponent,
  KcIconButtonComponent,
} from '@kaspacom/ui-kit';
import { formatSompi } from '../../shared/kcc20-formatters';
import { LadderDirection } from '../../shared/ladder-limit-orders.util';

export interface LadderLimitReviewRow {
  unitPriceSompi: string;
  notionalSompi: string;
}

export interface LadderLimitReviewDialogData {
  side: 'buy' | 'sell';
  ticker: string;
  tokenAmount: string;
  direction: LadderDirection;
  percent: string;
  rows: LadderLimitReviewRow[];
  totalNotionalSompi: string;
  settlementSompi: string;
  confirmLabel: string;
  kasDisplayMaxDecimals: number;
  onConfirm: (unitPriceSompi: string[]) => void;
}

interface LadderReviewRowVm extends LadderLimitReviewRow {
  id: number;
}

/**
 * Pre-submit review for a resting-limit ladder, modeled on Kaspiano's batch
 * sell confirmation: show every order, let the user drop a step, then confirm.
 * Confirm enqueues the remaining orders into this app's wallet-operations
 * queue instead of walking the wallet one-by-one inside the modal.
 */
@Component({
  selector: 'app-ladder-limit-review-dialog',
  standalone: true,
  imports: [KcDialogComponent, KcButtonComponent, KcIconButtonComponent],
  templateUrl: './ladder-limit-review-dialog.component.html',
  styleUrl: './ladder-limit-review-dialog.component.scss',
})
export class LadderLimitReviewDialogComponent {
  protected readonly data = inject<LadderLimitReviewDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef<void>);

  private nextRowId = 0;
  protected readonly rows = signal<LadderReviewRowVm[]>(
    this.data.rows.map((row) => ({ ...row, id: ++this.nextRowId })),
  );

  protected readonly remainingCount = computed(() => this.rows().length);
  protected readonly remainingNotionalSompi = computed(() =>
    this.rows()
      .reduce((sum, row) => sum + BigInt(row.notionalSompi), 0n)
      .toString(),
  );
  protected readonly title = computed(() =>
    this.data.side === 'buy' ? 'Review buy orders' : 'Review sell orders',
  );
  protected readonly stepLabel = computed(() => {
    const percent = this.data.percent.trim() || '0';
    if (percent === '0') {
      return 'Same price';
    }
    return `${percent}% ${this.data.direction}`;
  });
  protected readonly confirmLabel = computed(() => {
    const count = this.remainingCount();
    if (count <= 1) {
      return this.data.side === 'buy' ? 'Buy' : 'Sell';
    }
    return this.data.side === 'buy'
      ? `Buy ${count} orders`
      : `Sell ${count} orders`;
  });

  protected formatSompi(value: string): string {
    return formatSompi(value, this.data.kasDisplayMaxDecimals);
  }

  protected removeRow(id: number): void {
    this.rows.update((rows) => rows.filter((row) => row.id !== id));
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected confirm(): void {
    const prices = this.rows().map((row) => row.unitPriceSompi);
    if (!prices.length) {
      return;
    }
    this.dialogRef.close();
    this.data.onConfirm(prices);
  }
}
